import { api } from './api';
import { toast } from 'sonner';

/**
 * Abre YA una pestaña en blanco, dentro del clic del usuario, para rellenarla
 * cuando llegue la URL (2026-08-05).
 *
 * Por qué: si se hace al revés —esperar al servidor y DESPUÉS `window.open`—
 * el navegador ya no lo considera parte del clic y lo bloquea sin avisar. Ese
 * era el "pulso y no pasa nada" de los botones de WhatsApp.
 */
export function abrirPestanaYa() {
  try {
    return window.open('about:blank', '_blank');
  } catch {
    return null;
  }
}

/**
 * Lleva la pestaña ya abierta a su destino. Si el navegador la bloqueó, deja
 * un aviso con un botón: pulsarlo SÍ es un gesto del usuario y abre bien.
 */
export function llevarPestanaA(ventana, url, textoAviso = 'Abrir WhatsApp') {
  if (ventana && !ventana.closed) {
    ventana.location.href = url;
    return;
  }
  toast('El navegador ha bloqueado la ventana', {
    description: 'Pulsa aquí para abrirlo.',
    duration: Infinity,
    closeButton: true,
    className: 'cp-toast-confirmacion',
    action: { label: textoAviso, onClick: () => window.open(url, '_blank', 'noopener') },
  });
}

/**
 * Mensaje de WhatsApp asistido de una cita. `tipo`:
 *  - 'confirmacion': al agendar — "su cita ha quedado registrada".
 *  - 'recordatorio': el día antes — "le recordamos su cita".
 */
export async function mensajeCita(citaId, tipo = 'recordatorio') {
  const res = await api.get(`/api/recordatorios/cita/${citaId}`, { params: { tipo } });
  return res.data;
}

/**
 * Deja constancia en la cita de que el WhatsApp ha salido (lo que el panel
 * Recordatorios pinta como «Enviado por WhatsApp»). Lo llaman el panel y, desde
 * el cierre 2026-09-05, también el botón Confirmación/Recordatorio del
 * resumen de la cita. La respuesta trae la marca tal como la ha guardado el
 * servidor (`recordatorio_enviado_at`, `recordatorio_canal`;
 * 2026-09-08, punto 14): quien llama pinta con ESO, no con la hora del
 * navegador ni con una recarga que puede adelantarse a la marca. Si falla,
 * quien llama avisa: la cita sigue pendiente de verdad.
 */
export function marcarWhatsappEnviado(citaId) {
  return api.post(`/api/recordatorios/${citaId}/whatsapp-enviado`);
}

/**
 * Cómo se nombra el canal en la etiqueta «Enviado por …» (punto 14: «Enviado
 * por WhatsApp»): antes salía el campo tal cual,
 * «Enviado (whatsapp)».
 */
const NOMBRE_CANAL = { whatsapp: 'WhatsApp', email: 'email' };
export function nombreCanal(canal) {
  return NOMBRE_CANAL[canal] || canal || '';
}

/**
 * Aviso al equipo cuando el saludo del mensaje sale «Estimado/a» (el backend
 * lo dice con `saludo_neutro`, cierre 2026-09-05): ni la ficha ni el nombre
 * deciden el género. No bloquea el envío; se pide revisar la ficha.
 */
export function avisarSaludoNeutro(nombre = '') {
  toast.warning('El saludo sale como «Estimado/a»: revisa el sexo o el nombre en la ficha', {
    description: nombre || undefined,
    duration: 10000,
  });
}

/**
 * Confirmación de un RETOQUE sin coste conocido (2026-09-08, punto
 * 8): el servidor busca el coste del retoque en la cita, los episodios y la
 * historia (`retoque_coste` en la respuesta de `mensajeCita`); si no lo
 * encuentra, el mensaje sale con el bloque «Gastos de quirófano» y aquí se
 * avisa, sin frenar el envío, para que recepción lo revise antes de mandar.
 */
export const AVISO_COSTE_RETOQUE = 'No se ha encontrado el coste del retoque en la historia: revisa antes de enviar';

/** Cita de retoque = su concepto lleva la marca `es_retoque` (la misma
 * regla que services/cita_conceptos.es_retoque en el backend). La marca es
 * de cada clínica y viene con el catálogo de conceptos; el código se compara
 * sin mayúsculas ni espacios, como en el servidor. */
export function esConceptoRetoque(code, conceptos = []) {
  const slug = (c) => String(c || '').trim().replace(/\s+/g, '-').toLowerCase();
  const buscado = slug(code);
  if (!buscado) return false;
  const concepto = (conceptos || []).find((c) => slug(c?.code) === buscado);
  return concepto?.es_retoque === true;
}

export function costeRetoqueDesconocido(respuesta) {
  return !!respuesta?.retoque_coste && respuesta.retoque_coste.con_coste === null;
}

export function avisarCosteRetoque(nombre = '') {
  toast.warning(AVISO_COSTE_RETOQUE, {
    description: nombre || undefined,
    duration: 12000,
  });
}

/**
 * El mismo mensaje que `mensajeCita`, para VARIAS citas de golpe (panel
 * Recordatorios de la vista del día, 2026-09-03, punto 3). Devuelve
 * {canal: 'api'|'asistido', resultados: [{cita_id, estado, …}]}: con la API
 * de Meta el servidor ya los ha mandado ('enviado'); sin ella vuelven
 * 'preparado' con su whatsapp_url para abrirlos uno a uno.
 *
 * `reenviar` solo cuando recepción ha marcado a mano una fila ya enviada:
 * sin él, el servidor omite las que ya tienen recordatorio («Ya enviado»),
 * para que un segundo clic no reenvíe a todo el día (verificación de
 * cierre 2026-09-05).
 */
export async function enviarMensajesMasivo(citaIds, tipo, reenviar = false) {
  const res = await api.post('/api/recordatorios/enviar-masivo', {
    cita_ids: citaIds, tipo, ...(reenviar ? { reenviar: true } : {}),
  });
  return res.data;
}

/**
 * Tras crear una cita: ofrece en un toast mandar el mensaje de CONFIRMACIÓN
 * por WhatsApp (distinto del recordatorio del día anterior). Best-effort: si
 * el paciente no tiene teléfono no se ofrece nada.
 */
export function ofrecerConfirmacionWhatsApp(citaId) {
  if (!citaId) return;
  mensajeCita(citaId, 'confirmacion')
    .then((d) => {
      if (!d?.whatsapp_url) return;
      // Aviso GRANDE y que no se va solo (2026-08-05): antes duraba
      // 12 s y se escapaba en mitad de una consulta. Ahora hay que cerrarlo a
      // mano, y si se cierra sin enviar, el botón "Confirmación" del resumen
      // de la cita lo manda igual — no se pierde.
      toast.success('Cita creada · mándale la confirmación', {
        description: 'Si ahora no puedes, la tienes en el resumen de la cita → botón «Confirmación».',
        duration: Infinity,
        closeButton: true,
        className: 'cp-toast-confirmacion',
        action: {
          label: 'Enviar por WhatsApp',
          onClick: () => window.open(d.whatsapp_url, '_blank', 'noopener'),
        },
      });
    })
    .catch(() => {});
}

// -----------------------------------------------------------------------------
// Phase 27 — Sistema de citas (agendas, conceptos, citas, bloqueos, episodios
// y sesiones de quirófano).
// -----------------------------------------------------------------------------
// Capa fina sobre `api` (axios con cookie httpOnly): cada función devuelve
// directamente `response.data` para que las páginas no repitan `.data` por
// todas partes. Los errores se propagan tal cual (axios) para que el caller
// pueda leer `err.response.data.detail` / `err.response.data.conflicts`.
// -----------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Agendas
// ---------------------------------------------------------------------------

/** Lista de agendas visibles para el usuario (ya filtrada/ordenada por rol). */
export async function listAgendas() {
  const res = await api.get('/api/agendas');
  return res.data;
}


/** Solo admin. payload: {name?, color?, slot_minutes?, working_hours?, concept_overrides?} */
export async function updateAgenda(agendaId, payload) {
  const res = await api.put(`/api/agendas/${agendaId}`, payload);
  return res.data;
}

// ---------------------------------------------------------------------------
// Bloqueos de agenda
// ---------------------------------------------------------------------------

export async function listBloqueos(agendaId, { dateFrom, dateTo } = {}) {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await api.get(`/api/agendas/${agendaId}/bloqueos`, { params });
  return res.data;
}

/** Solo admin. payload: {date_from, date_to, all_day, start?, end?, reason?} */
export async function createBloqueo(agendaId, payload) {
  // agenda_id también en el body (el backend usa el del path, pero así la
  // petición es autosuficiente y compatible con el modelo).
  const res = await api.post(`/api/agendas/${agendaId}/bloqueos`, { agenda_id: agendaId, ...payload });
  return res.data;
}

/** Solo admin. */
export async function deleteBloqueo(bloqueoId) {
  const res = await api.delete(`/api/agendas/bloqueos/${bloqueoId}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Día de quirófano — el lugar (y cirujano en externos) es de TODO el
// quirófano de ese día; las citas lo heredan.
// ---------------------------------------------------------------------------

/** Config del día ({} si aún no está fijada). */
export async function getQuirofanoDia(agendaId, date) {
  const res = await api.get(`/api/agendas/${agendaId}/dia-quirofano`, { params: { date } });
  return res.data;
}

/** Fija o cambia lugar/cirujano del día; se propaga a las citas existentes. */
export async function setQuirofanoDia(agendaId, { date, lugar, cirujano, anestesista }) {
  const res = await api.put(`/api/agendas/${agendaId}/dia-quirofano`, { date, lugar, cirujano, anestesista });
  return res.data;
}

/** Consulta conjunta: marcar/desmarcar una cita como vista por enfermería. */
export async function setVistoEnfermeria(citaId, visto = true) {
  const res = await api.post(`/api/citas/${citaId}/visto-enfermeria`, { visto });
  return res.data;
}

/** Flujo del día: null o un estado permitido por el tipo de agenda. */
export async function setEstadoCita(citaId, estado) {
  const res = await api.post(`/api/citas/${citaId}/estado`, { estado });
  return res.data;
}

/** Etiquetas y colores del estado del paciente en el día. */
export const ESTADOS_CITA = [
  { value: 'espera', label: 'Sala de espera', color: '#D97706' },
  { value: 'tratamiento', label: 'Consulta', color: '#2563EB' },
  { value: 'postop_ok', label: 'Listo para cambiarse', color: '#7C3AED' },
  { value: 'realizada', label: 'Realizada (en recuperación)', color: '#059669' },
  { value: 'se_fue', label: 'Se fue', color: '#64748B' },
];

/**
 * Estados que se ofrecen según la agenda (2026-07-28).
 * "Listo para cambiarse", "en recuperación" y "se fue" son de quirófano; en
 * consulta solo hay tres pasos: sala de espera → en consulta → realizada.
 */
export function estadosDeAgenda(agenda) {
  const esQuirofano = (agenda?.tipo || agenda?.type) === 'quirofano';
  if (esQuirofano) {
    return ESTADOS_CITA
      .filter((e) => ['postop_ok', 'realizada', 'se_fue'].includes(e.value))
      .map((e) => (e.value === 'realizada' ? { ...e, label: 'En recuperación' } : e));
  }
  return ESTADOS_CITA
    .filter((e) => ['espera', 'tratamiento', 'realizada'].includes(e.value))
    .map((e) => (e.value === 'realizada' ? { ...e, label: 'Realizada' } : e));
}

/**
 * ¿La agenda DE ESTA CITA tiene consulta conjunta (enfermería ve primero al
 * paciente)? Es lo que decide el check y el botón de «visto por enfermería».
 *
 * Se mira la agenda de la cita y no la que está abierta (24/9/2026): en la
 * agenda de Enfermería, las citas prestadas de otra agenda se quedaban sin
 * el botón de enfermería porque Enfermería no tiene consulta
 * conjunta. El servidor (`set_visto_enfermeria`) ya miraba la de la cita.
 *
 * `agendas`: las que tenga cargadas la pantalla (la abierta primero). Si la
 * de la cita no está entre ellas, vale lo que dice el listado de citas en
 * `consulta_conjunta_de_su_agenda`; sin ninguna de las dos cosas, no se
 * ofrece (el servidor lo rechazaría igual).
 */
export function consultaConjuntaDeLaCita(cita, agendas = []) {
  if (!cita) return false;
  const suya = (agendas || []).find((a) => a && a.id && a.id === cita.agenda_id);
  if (suya) return (suya.tipo || suya.type) !== 'quirofano' && !!suya.consulta_conjunta;
  return cita.consulta_conjunta_de_su_agenda === true;
}

// ---------------------------------------------------------------------------
// Notas internas de paciente — SOLO administración (el backend lo exige).
// ---------------------------------------------------------------------------

export async function getNotasInternas(patientId) {
  const res = await api.get(`/api/patients/${patientId}/notas-internas`);
  return res.data;
}

export async function setNotasInternas(patientId, texto) {
  const res = await api.put(`/api/patients/${patientId}/notas-internas`, { texto });
  return res.data;
}

/** Marcador CON/SIN IVA del tratamiento (Phase 27.21). iva: 'con' | 'sin'. */

/** Notas clínicas (Phase 27.20): privadas del autor + compartidas doctores/admin. */
export async function getNotasPrivadas(patientId) {
  const res = await api.get(`/api/patients/${patientId}/notas-privadas`);
  return res.data;
}
export async function setNotasPrivadas(patientId, texto) {
  const res = await api.put(`/api/patients/${patientId}/notas-privadas`, { texto });
  return res.data;
}
export async function getNotasCompartidas(patientId) {
  const res = await api.get(`/api/patients/${patientId}/notas-compartidas`);
  return res.data;
}
export async function setNotasCompartidas(patientId, texto) {
  const res = await api.put(`/api/patients/${patientId}/notas-compartidas`, { texto });
  return res.data;
}

// ---------------------------------------------------------------------------
// Conceptos de cita
// ---------------------------------------------------------------------------

/**
 * Tipos de cita. Sin opciones, solo los ACTIVOS (lo que ofrecen los
 * desplegables de alta). Con `{ todos: true }` también los retirados
 * (active=false) con su flag (revisión 2026-09-03): la agenda los necesita
 * para pintar con su color y nombre las citas ya dadas con un tipo retirado
 * («Cirugía» y «Cirugía menor» tras el punto 4), y Gestión de agendas para
 * poder reactivarlos.
 */
export async function listConceptos({ todos = false } = {}) {
  const res = await api.get('/api/cita-conceptos', todos ? { params: { todos: 1 } } : undefined);
  return res.data;
}

/** Los conceptos que se pueden ELEGIR en un desplegable: activos y, si la
 * cita ya lleva un tipo retirado, ese también (marcado) para que el
 * desplegable no quede en blanco. */
export function conceptosElegibles(conceptos = [], codeActual = null) {
  return conceptos.filter((c) => c.active !== false || c.code === codeActual);
}

/** Nombre del concepto para un desplegable: «Cirugía (retirado)» si ya no
 * se ofrece. */
export function nombreConcepto(concepto) {
  if (!concepto) return '';
  return concepto.active === false ? `${concepto.name} (retirado)` : concepto.name;
}

/** Solo admin. payload: {code (slug único), name, category, default_minutes, color?, sort_order?} */
export async function createConcepto(payload) {
  const res = await api.post('/api/cita-conceptos', payload);
  return res.data;
}

/** Solo admin. payload: {name?, category?, default_minutes?, color?, sort_order?} */
export async function updateConcepto(conceptoId, payload) {
  const res = await api.put(`/api/cita-conceptos/${conceptoId}`, payload);
  return res.data;
}

/** Solo admin. Soft-delete (active=false): las citas existentes no se ven afectadas. */
export async function deleteConcepto(conceptoId) {
  const res = await api.delete(`/api/cita-conceptos/${conceptoId}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Citas
// ---------------------------------------------------------------------------

/**
 * Lista citas. Combinaciones soportadas por el backend:
 *   { agendaId, date }                — día concreto de una agenda
 *   { agendaId, dateFrom, dateTo }    — rango de una agenda
 *   { patientId }                     — historial de un paciente
 * Añade `includeCancelled: true` para incluir canceladas.
 * Añade `includeNoAcude: true` para traer también las «no acude» (la agenda
 * las deja en su hueco con el nombre tachado; 2026-09-08, punto 12).
 */
export async function listCitas({ agendaId, date, dateFrom, dateTo, patientId, includeCancelled, includeNoAcude } = {}) {
  const params = {};
  if (agendaId) params.agenda_id = agendaId;
  if (date) params.date = date;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (patientId) params.patient_id = patientId;
  if (includeCancelled) params.include_cancelled = true;
  if (includeNoAcude) params.include_no_acude = true;
  const res = await api.get('/api/citas', { params });
  return res.data;
}

/**
 * Huecos libres → {slots: [{start, end}], duration_minutes}.
 * `durationMinutes` fuerza una duración distinta a la del concepto;
 * `excludeCitaId` ignora una cita existente (para mover/editar).
 * `patientId` (23/9/2026): no ofrece los huecos que dejarían a ese paciente en
 * dos sitios a la vez (una cita de quirófano); el servidor los rechazaría.
 */
export async function freeSlots({ agendaId, date, conceptCode, durationMinutes, excludeCitaId, patientId }) {
  const params = { agenda_id: agendaId, date, concept_code: conceptCode };
  if (durationMinutes) params.duration_minutes = durationMinutes;
  if (excludeCitaId) params.exclude_cita_id = excludeCitaId;
  if (patientId) params.patient_id = patientId;
  const res = await api.get('/api/citas/free-slots', { params });
  return res.data;
}

/**
 * Primer hueco libre a partir de hoy (o de dateFrom) →
 * {found, date, start, end, duration_minutes} o {found: false}.
 */
export async function primerHueco({ agendaId, conceptCode, durationMinutes, dateFrom, excludeCitaId, patientId }) {
  const params = { agenda_id: agendaId };
  if (conceptCode) params.concept_code = conceptCode;
  if (durationMinutes) params.duration_minutes = durationMinutes;
  if (dateFrom) params.date_from = dateFrom;
  if (excludeCitaId) params.exclude_cita_id = excludeCitaId;
  if (patientId) params.patient_id = patientId; // mismo criterio que freeSlots
  const res = await api.get('/api/citas/primer-hueco', { params });
  return res.data;
}

/**
 * payload: {agenda_id, patient_id, concept_code, date, start, notes?}
 * 409 si solapa → err.response.data = {detail, conflicts}.
 */
export async function createCita(payload) {
  const res = await api.post('/api/citas', payload);
  return res.data;
}

/** payload: {date?, start?, concept_code?, notes?, ...} — mover/editar. */
export async function updateCita(citaId, payload) {
  const res = await api.put(`/api/citas/${citaId}`, payload);
  return res.data;
}

/** Marca que el paciente no se presentó (queda en su histórico como «No acude»). */
export async function marcarNoAcude(citaId) {
  const { data } = await api.post(`/api/citas/${citaId}/no-acude`);
  return data;
}

/** Marcha atrás de un «no acude» marcado por error: la cita vuelve a activa. */
export async function deshacerNoAcude(citaId) {
  const { data } = await api.post(`/api/citas/${citaId}/no-acude/deshacer`);
  return data;
}

export async function cancelCita(citaId, reason = '') {
  const res = await api.post(`/api/citas/${citaId}/cancel`, { reason });
  return res.data;
}

// ---------------------------------------------------------------------------
// Episodios (historia evolutiva)
// ---------------------------------------------------------------------------

/** `patientId` es obligatorio (no hay listado global). */
export async function listEpisodios(patientId) {
  const res = await api.get('/api/episodios', { params: { patient_id: patientId } });
  return res.data;
}

/** Idempotente: devuelve el episodio abierto del doctor para paciente+fecha si ya existe. */
export async function createEpisodio(payload) {
  const res = await api.post('/api/episodios', payload);
  return res.data;
}

/**
 * Guarda el texto del evolutivo y devuelve el episodio ya guardado (con su
 * `updated_at` nuevo).
 *
 * `payload.visto_updated_at` es el sello de la versión que el editor tenía
 * delante. Desde 2026-09-17 el médico en consulta y un admin de mostrador
 * pueden estar en la misma nota a la vez, y este PUT manda el texto ENTERO:
 * sin el sello, el último en guardar borraba lo del otro en silencio. Si el
 * episodio ha cambiado por debajo el servidor NO escribe nada y contesta 409
 * con `{detail, conflicto: {texto, updated_at, quien, status}}` — el texto que
 * hay guardado ahora, para poder enseñar las dos versiones en vez de tragarse
 * la pérdida. El error sube tal cual (axios): lo atiende HistoriaClinicaTab.
 */
export async function updateEpisodio(episodioId, payload) {
  const res = await api.put(`/api/episodios/${episodioId}`, payload);
  return res.data;
}

export async function closeEpisodio(episodioId) {
  const res = await api.post(`/api/episodios/${episodioId}/close`);
  return res.data;
}


/**
 * Pasa el dictado a limpio con la IA, que aprende del propio doctor mirando
 * sus evolutivos anteriores. NO guarda: devuelve la propuesta para revisar.
 */
export async function pasarEpisodioALimpio(episodioId, texto) {
  const res = await api.post(`/api/episodios/${episodioId}/pasar-a-limpio`, { texto });
  return res.data; // {texto, ejemplos_usados}
}

/** Solo admin. */
/** Borra un episodio de la historia (solo quien tiene el permiso).
 *  Sin `aun_asi`, el servidor contesta 409 si arrastra presupuesto o actos de
 *  colaborador, para que se vea antes de decidir. */
export async function borrarEpisodio(episodioId, opciones = {}) {
  const res = await api.delete(`/api/episodios/${episodioId}`, { data: opciones });
  return res.data;
}

export async function reopenEpisodio(episodioId) {
  const res = await api.post(`/api/episodios/${episodioId}/reopen`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Sesiones de quirófano
// ---------------------------------------------------------------------------

export async function listSesionesQuirofano({ dateFrom, dateTo } = {}) {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await api.get('/api/quirofano-sesiones', { params });
  return res.data;
}

export async function createSesionQuirofano(payload) {
  const res = await api.post('/api/quirofano-sesiones', payload);
  return res.data;
}

export async function updateSesionQuirofano(sesionId, payload) {
  const res = await api.put(`/api/quirofano-sesiones/${sesionId}`, payload);
  return res.data;
}


// ---------------------------------------------------------------------------
// Helpers de tiempo/fecha (fechas de negocio SIEMPRE en local, "YYYY-MM-DD")
// ---------------------------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, '0');

/** "HH:MM" → minutos desde medianoche. toMin('09:30') === 570. */
export function toMin(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = String(hhmm).split(':');
  return (Number(h) || 0) * 60 + (Number(m) || 0);
}

/** minutos desde medianoche → "HH:MM". toHHMM(570) === '09:30'. */
export function toHHMM(min) {
  const total = Math.max(0, Math.round(Number(min) || 0));
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
}

/** Date → "YYYY-MM-DD" en hora local (NO usar toISOString: cambia de día por UTC). */
export function dateToStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** "YYYY-MM-DD" → Date local a medianoche. */
export function strToDate(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** Hoy en local, "YYYY-MM-DD". */
export function todayStr() {
  return dateToStr(new Date());
}

/** Suma n días (puede ser negativo) a una fecha "YYYY-MM-DD". */
export function addDaysStr(dateStr, n) {
  const d = strToDate(dateStr);
  d.setDate(d.getDate() + n);
  return dateToStr(d);
}

// ---------------------------------------------------------------------------
// Constantes de dominio
// ---------------------------------------------------------------------------

/** Índice 0 = Lunes (mismo criterio que working_hours del backend). */
export const WEEKDAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export const SHIFT_LABELS = { manana: 'Mañana', tarde: 'Tarde' };

/** Color de un concepto con fallback corporativo. */
export function conceptColor(concepto) {
  return concepto?.color || '#2E6E5E';
}

/**
 * Minutos de un tipo de cita en una agenda: el override de la agenda si lo
 * hay, si no la duración por defecto del concepto (la misma regla que
 * resolve_duration en el backend). Es lo que se rellena solo en el campo
 * «Duración» al elegir el tipo de cita (2026-09-03). null si no
 * hay concepto o no tiene minutos.
 */
export function minutosConcepto(agenda, concepto) {
  if (!concepto) return null;
  return agenda?.concept_overrides?.[concepto.code] || concepto.default_minutes || null;
}

/** Lista de espera (Phase 27.18) — modo "quiere adelantar". */
export async function apuntarListaEspera(payload) {
  const res = await api.post('/api/lista-espera', payload); // {cita_id} o {patient_id, agenda_id, concept_code}
  return res.data;
}
export async function listarListaEspera(params = {}) {
  const res = await api.get('/api/lista-espera', { params });
  return res.data.items;
}
export async function huecosLiberados(params = {}) {
  const res = await api.get('/api/lista-espera/huecos-liberados', { params });
  return res.data.huecos;
}
export async function actualizarListaEspera(entradaId, payload) {
  const res = await api.put(`/api/lista-espera/${entradaId}`, payload);
  return res.data;
}
export async function borrarListaEspera(entradaId) {
  const res = await api.delete(`/api/lista-espera/${entradaId}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Hoja de anestesia DIGITAL (Registro de quirófano) — por paciente + fecha.
// ---------------------------------------------------------------------------
export async function listAnesthesiaRecords({ patientId, surgeryDate } = {}) {
  const params = {};
  if (patientId) params.patient_id = patientId;
  if (surgeryDate) params.surgery_date = surgeryDate;
  const res = await api.get('/api/anesthesia-records', { params });
  return res.data;
}
export async function upsertAnesthesiaRecord({ patientId, surgeryDate, citaId, agendaId, formData }) {
  const res = await api.post('/api/anesthesia-records', {
    patient_id: patientId,
    surgery_date: surgeryDate,
    cita_id: citaId || null,
    agenda_id: agendaId || null,
    form_data: formData || {},
  });
  return res.data;
}
export async function saveAnesthesiaRecord(recordId, formData, { signed } = {}) {
  const body = { form_data: formData };
  if (signed != null) body.signed = signed;
  const res = await api.put(`/api/anesthesia-records/${recordId}`, body);
  return res.data;
}

// ---------------------------------------------------------------------------
// Tiempo de cirugía (inicio/fin) apuntado en la vista de Quirófano. El informe
// «Registro de externos» ya no se lee de aquí: sale del Registro de quirófano
// (GET /api/registro-externos, 2026-09-08, punto 6); estos tiempos le
// sirven de respaldo cuando la intervención del registro no lleva horas.
// ---------------------------------------------------------------------------
export async function setCirugiaTiempos(citaId, { inicio, fin }) {
  const res = await api.put(`/api/citas/${citaId}/cirugia-tiempos`, {
    inicio: inicio || null,
    fin: fin || null,
  });
  return res.data;
}
